"use client";

import { c as _c } from "react/compiler-runtime";
import { IconShell } from "@/components/ui/glass-icon";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { cn } from "@/lib/utils";
import { resolveNodeDefinition, type CardIcon, type CardIconChainInfo, type CardIconConnectorType, type CardIconDef } from "@/lib/workflow/cardIcons";
import { getRequiredConnectorType } from "@/utils/workflowTransform";
import * as React from "react";
interface CardIconTileProps {
  icon: CardIcon;
  triggers: CardIconDef[];
  actions: CardIconDef[];
  connectorTypes: CardIconConnectorType[];
  chainInfoMap?: Map<number, CardIconChainInfo>;
  /**
   * Visual template used when the node has no logoUrl and falls back to
   * AI-generated NodeIcon. Template cards use `b3os-app-flat` to match the
   * detail-page hero; workflow cards use the default (line-icon).
   */
  nodeTemplate?: "line-icon" | "moebius-icon" | "b3os-app-flat";
  /** Tile outer wrapper class (size/padding). */
  wrapClassName?: string;
  /** Inner plate class (background/border/rounded) — forwarded to IconShell which requires it. */
  innerClassName: string;
  /** Class applied to the rendered logo/icon element. */
  iconClassName?: string;
}
function ConnectorLogo(t0) {
  const $ = _c(5);
  const {
    brandName,
    className
  } = t0;
  const [error, setError] = React.useState(false);
  if (error) {
    return null;
  }
  const t1 = `https://ai-icon.b3.fun/logo/${encodeURIComponent(brandName.replace(/\s+/g, "_"))}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setError(true);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== brandName || $[2] !== className || $[3] !== t1) {
    t3 = <img src={t1} alt={brandName} title={brandName} className={className} onError={t2} />;
    $[1] = brandName;
    $[2] = className;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * One tile of a template/workflow card's icon strip. For connector/chain
 * icons (which are plain <img> tags) we render the 3-layer chrome inline;
 * for nodes we delegate to NodeIconTile so the NodeIcon resolution path
 * (def.logoUrl + AI-icon fallback) stays centralised.
 */
export function CardIconTile(t0) {
  const $ = _c(54);
  const {
    icon,
    triggers,
    actions,
    connectorTypes,
    chainInfoMap,
    nodeTemplate,
    wrapClassName,
    innerClassName,
    iconClassName
  } = t0;
  if (icon.kind === "connector") {
    let t1;
    if ($[0] !== icon.brandName || $[1] !== iconClassName) {
      t1 = <ConnectorLogo brandName={icon.brandName} className={iconClassName} />;
      $[0] = icon.brandName;
      $[1] = iconClassName;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== innerClassName || $[4] !== t1) {
      t2 = <IconShell className={innerClassName}>{t1}</IconShell>;
      $[3] = innerClassName;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== t2 || $[7] !== wrapClassName) {
      t3 = <div className={wrapClassName}>{t2}</div>;
      $[6] = t2;
      $[7] = wrapClassName;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (icon.kind === "chain") {
    let chain;
    let t1;
    if ($[9] !== chainInfoMap || $[10] !== icon.chainId) {
      chain = chainInfoMap?.get(icon.chainId);
      t1 = chain?.iconUrl || (chain?.name ? `https://ai-icon.b3.fun/logo/${encodeURIComponent(chain.name.replace(/\s+/g, "_"))}` : null);
      $[9] = chainInfoMap;
      $[10] = icon.chainId;
      $[11] = chain;
      $[12] = t1;
    } else {
      chain = $[11];
      t1 = $[12];
    }
    const chainIconUrl = t1;
    if (!chainIconUrl) {
      let t2;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <span />;
        $[13] = t2;
      } else {
        t2 = $[13];
      }
      let t3;
      if ($[14] !== innerClassName) {
        t3 = <IconShell className={innerClassName}>{t2}</IconShell>;
        $[14] = innerClassName;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      let t4;
      if ($[16] !== t3 || $[17] !== wrapClassName) {
        t4 = <div className={wrapClassName}>{t3}</div>;
        $[16] = t3;
        $[17] = wrapClassName;
        $[18] = t4;
      } else {
        t4 = $[18];
      }
      return t4;
    }
    const t2 = chain?.name || `Chain ${icon.chainId}`;
    let t3;
    if ($[19] !== chainIconUrl || $[20] !== iconClassName || $[21] !== t2) {
      t3 = <img src={chainIconUrl} alt={t2} className={iconClassName} onError={_temp} />;
      $[19] = chainIconUrl;
      $[20] = iconClassName;
      $[21] = t2;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    let t4;
    if ($[23] !== innerClassName || $[24] !== t3) {
      t4 = <IconShell className={innerClassName}>{t3}</IconShell>;
      $[23] = innerClassName;
      $[24] = t3;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    let t5;
    if ($[26] !== t4 || $[27] !== wrapClassName) {
      t5 = <div className={wrapClassName}>{t4}</div>;
      $[26] = t4;
      $[27] = wrapClassName;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    return t5;
  }
  let def;
  let t1;
  if ($[29] !== actions || $[30] !== icon.isTrigger || $[31] !== icon.type || $[32] !== triggers) {
    def = resolveNodeDefinition(icon.type, icon.isTrigger, triggers, actions);
    t1 = getRequiredConnectorType(icon.type, def);
    $[29] = actions;
    $[30] = icon.isTrigger;
    $[31] = icon.type;
    $[32] = triggers;
    $[33] = def;
    $[34] = t1;
  } else {
    def = $[33];
    t1 = $[34];
  }
  const reqConnType = t1;
  let t2;
  if ($[35] !== connectorTypes || $[36] !== reqConnType) {
    t2 = reqConnType ? connectorTypes.find(ct => ct.type === reqConnType) : undefined;
    $[35] = connectorTypes;
    $[36] = reqConnType;
    $[37] = t2;
  } else {
    t2 = $[37];
  }
  const ctDef = t2;
  let t3;
  if ($[38] !== ctDef?.logoUrl || $[39] !== reqConnType) {
    t3 = reqConnType ? {
      type: reqConnType,
      logoUrl: ctDef?.logoUrl
    } : undefined;
    $[38] = ctDef?.logoUrl;
    $[39] = reqConnType;
    $[40] = t3;
  } else {
    t3 = $[40];
  }
  const connector = t3;
  const color = icon.isTrigger ? "#2450FF" : "#22c55e";
  const t4 = icon.type;
  const t5 = def?.name || icon.type;
  const t6 = def?.logoUrl;
  const t7 = def?.name || icon.type;
  let t8;
  if ($[41] !== iconClassName) {
    t8 = cn("flex-shrink-0", iconClassName);
    $[41] = iconClassName;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  let t9;
  if ($[43] !== color || $[44] !== connector || $[45] !== icon.type || $[46] !== innerClassName || $[47] !== nodeTemplate || $[48] !== t5 || $[49] !== t6 || $[50] !== t7 || $[51] !== t8 || $[52] !== wrapClassName) {
    t9 = <NodeIconTile id={t4} subject={t5} context="" logoUrl={t6} connector={connector} template={nodeTemplate} alt={t7} color={color} wrapClassName={wrapClassName} innerClassName={innerClassName} iconClassName={t8} />;
    $[43] = color;
    $[44] = connector;
    $[45] = icon.type;
    $[46] = innerClassName;
    $[47] = nodeTemplate;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t8;
    $[52] = wrapClassName;
    $[53] = t9;
  } else {
    t9 = $[53];
  }
  return t9;
}
function _temp(e) {
  e.currentTarget.style.display = "none";
}